import { useState, useEffect, useRef } from 'react';
import { useSearch, useLocation } from 'wouter';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useCoachingSessionStore } from '../lib/coachingSessionStore';
import { getChatSession, createChatSession, API_BASE } from '../lib/api';
import CoachingWorkspacePage from './CoachingWorkspacePage';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { normalizeCoachId, normalizeCoachIds } from '../lib/coachIdMap';

const DEFAULT_PRIMARY_COACH = 'samuel';

const CoachingSessionPage = () => {
  const searchParams = useSearch();
  const { getToken } = useAuth();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get session ID from URL query parameters
  const urlParams = new URLSearchParams(searchParams);
  const sessionId = urlParams.get('session');
  const coachesParam = urlParams.get('coaches');

  // Access the coaching session store
  const {
    setSessionData,
    setSelectedCoachIds,
    sessionId: storeSessionId
  } = useCoachingSessionStore();

  const [, navigate] = useLocation();

  // Fetch session data when component mounts
  const hasLoadedRef = useRef<string | null>(null);
  const isCreatingRef = useRef(false);

  const { data: profile } = useCurrentUserProfile();

  const preferredCoach = normalizeCoachId(
    (profile?.preferred_coach as string) ||
    (user?.publicMetadata?.preferred_coach as string) ||
    (profile?.recommended_coach as string) ||
    DEFAULT_PRIMARY_COACH,
  );

  useEffect(() => {
    // Only run if sessionId is new
    if (sessionId && hasLoadedRef.current === sessionId) return;
    if (!sessionId && isCreatingRef.current) return;

    hasLoadedRef.current = sessionId || null;

    const fetchSessionData = async () => {
      try {
        setIsLoading(true);
        const token = await getToken();

        if (!token) {
          throw new Error('Authentication token not available');
        }

        // If no session provided, check if we have one in store or if we should create a new one
        if (!sessionId) {
          // If we already have a sessionId in the store, use it instead of creating a new one
          if (storeSessionId) {
            navigate(`/coaching/session?session=${storeSessionId}`);
            return;
          }

          // Recovery fix: Check localStorage for the most recent active session
          const lastSessionId = localStorage.getItem('hipo_last_coaching_session_id');
          if (lastSessionId && !coachesParam) {
            console.log('Recovering last active session:', lastSessionId);
            navigate(`/coaching/session?session=${lastSessionId}`);
            return;
          }

          // ONLY create a new session if we have coach parameters (e.g. from Selection page selection)
          // or if we really have no other choice.
          let coachIds: string[];
          if (coachesParam) {
            coachIds = normalizeCoachIds(coachesParam.split(',').map(c => c.trim()).filter(Boolean));
          } else {
            // Default: Preferred coach + EmpathEAR (emotional support)
            const empathEarId = 'arjun';
            const stressLevel = (user?.publicMetadata as any)?.stress || 'normal';
            const defaultCoachPair = stressLevel === 'high'
              ? [empathEarId, preferredCoach]
              : [preferredCoach, empathEarId];
            coachIds = normalizeCoachIds(defaultCoachPair);
          }

          if (coachIds.length > 0 && coachesParam) {
            isCreatingRef.current = true;
            const title = 'Coaching Session';
            const newSession = await createChatSession(coachIds, token, title);
            isCreatingRef.current = false;

            if (newSession) {
              setSessionData(newSession.id, newSession.title || title);
              setSelectedCoachIds(newSession.coachTypes || coachIds);
              localStorage.setItem('hipo_last_coaching_session_id', newSession.id);
              navigate(`/coaching/session?session=${newSession.id}`);
            } else {
              setError('Failed to create new session');
            }
          } else if (!coachesParam) {
            // If no params and no session, go back to selection
            navigate('/coaching');
          } else {
            setError('No coach IDs available to create session');
          }
          return;
        }

        // Session ID provided - fetch existing session
        const session = await getChatSession(sessionId, token);

        if (session) {
          setSessionData(session.id, session.title || 'Coaching Session');
          
          if (session.mom_data) {
            // If session is already completed, it shouldn't be the "last active" session
            localStorage.removeItem('hipo_last_coaching_session_id');
          } else {
            localStorage.setItem('hipo_last_coaching_session_id', session.id);
          }

          if (session.coachTypes && session.coachTypes.length > 0) {
            setSelectedCoachIds(normalizeCoachIds(session.coachTypes));
          }
        } else {
          setError(`Session ${sessionId} not found`);
        }
      } catch (err) {
        console.error('Error in session handling:', err);
        setError('Failed to load session data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId, getToken, setSessionData, setSelectedCoachIds, navigate, coachesParam, profile, user]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
          </div>
          <p className="mt-4 text-sm text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
          <h2 className="mb-4 text-xl font-semibold text-red-600">Session Error</h2>
          <p className="mb-6 text-slate-600">{error}</p>
          <a
            href="/coaching"
            className="inline-flex items-center justify-center rounded-md bg-royal-blue px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-royal-blue/90"
          >
            Return to Coaching
          </a>
        </div>
      </div>
    );
  }

  // Render the coaching workspace with the session data
  return <CoachingWorkspacePage />;
};

export default CoachingSessionPage;
